// src/pages/personal/analytics/FeedbackSection/FeedbackSection.tsx
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Flex, Card, Grid } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import FeedbackMetrics from './components/FeedbackMetrics';
import CategoryDistribution from './components/CategoryDistribution/CategoryDistribution';
import RecommendationStats from './components/RecommendationStats/RecommendationStats';
import CommentsAnalysis from './components/CommentsAnalysis/CommentsAnalysis';
import { PeriodSelector, Period, DateRange } from '@/components/ui/PeriodSelector';
import { useFeedbackData } from './hooks/useFeedbackData';
import { useResponsive } from '@/hooks/useResponsive';
import { formatSmartDate } from '@/utils/dateUtils';
import styles from './FeedbackSection.module.css';

// Интерфейсы типов данных
interface WordData {
  word: string;
  count: number;
}

interface CommentData {
  id: string;
  text: string;
  rating: number;
  date: string;
  platform: string;
}

interface CommentsData {
  total: number;
  percentage_with_comments: number;
  top_words: WordData[];
  comments: CommentData[]; 
}

// Определяем тип для метрик
type FeedbackMetricKey = 'product_quality' | 'service' | 'value' | 'recommend_percentage';

const FeedbackSection: React.FC = () => {
  const { t, i18n } = useTranslation('analytics');
  const [period, setPeriod] = useState<Period>('month');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const { isMobile } = useResponsive();
  
  // Добавляем состояние для выбранной метрики
  const [selectedMetric, setSelectedMetric] = useState<FeedbackMetricKey>('product_quality');
  
  // Фиксируем WhatsApp как единственную платформу
  const platforms = ['whatsapp'];
  
  // Используем useMemo для предотвращения бесконечных перерендеров
  const feedbackParams = useMemo(() => ({
    period,
    platforms,
    dateRange
  }), [period, dateRange]); // зависимость от period и dateRange
  
  const {
    data,
    isLoading,
    error,
    refreshData
  } = useFeedbackData(feedbackParams);

  // Форматируем период для отображения в заголовке
  const getFormattedPeriod = () => {
    if (dateRange && period === 'custom') {
      return `${formatSmartDate(dateRange.startDate.toISOString(), false, i18n.language)} - ${formatSmartDate(dateRange.endDate.toISOString(), false, i18n.language)}`;
    }

    // Для стандартных периодов используем перевод
    return t(`periods.${period}`);
  };
  
  // Обработчик выбора метрики
  const handleMetricSelect = (metric: FeedbackMetricKey) => {
    // Если выбрана метрика "recommend_percentage", не меняем график (она не влияет на график)
    if (metric !== 'recommend_percentage') {
      setSelectedMetric(metric);
    }
  };
  
  // ИСПРАВЛЕНО: Переработан обработчик смены периода, чтобы избежать бесконечного цикла
  const handlePeriodChange = useCallback((newPeriod: Period, newDateRange?: DateRange) => {
    // Устанавливаем новый период только если он действительно изменился
    if (newPeriod !== period) {
      setPeriod(newPeriod);
    }
    
    // Устанавливаем новый dateRange только если он действительно изменился
    if (newDateRange && 
        (!dateRange || 
         newDateRange.startDate.getTime() !== dateRange.startDate.getTime() || 
         newDateRange.endDate.getTime() !== dateRange.endDate.getTime())) {
      setDateRange(newDateRange);
    }
    
    // Запускаем обновление данных только если период или dateRange действительно изменились
    if (newPeriod !== period || 
        (newDateRange && 
         (!dateRange || 
          newDateRange.startDate.getTime() !== dateRange.startDate.getTime() || 
          newDateRange.endDate.getTime() !== dateRange.endDate.getTime()))) {
      // Используем setTimeout для предотвращения возможных проблем с React 18 Strict Mode
      setTimeout(() => {
        refreshData();
      }, 50);
    }
  }, [period, dateRange, refreshData]);
  
  // Преобразование выбранной метрики в категорию для графика
  const getCategoryFromMetric = (metric: FeedbackMetricKey): string => {
    // Для метрик product_quality, service и value используем прямое соответствие
    // Для recommend_percentage не меняем график, поэтому возвращаем product_quality
    return metric === 'recommend_percentage' ? 'product_quality' : metric;
  };
  
  return (
    <Container size="full" padding={isMobile ? "sm" : "md"}>
      <Flex direction="column" gap="md">
        <div className={styles.pageHeader}>
          <h1 className={TEXT_STYLES.h1}>{t('feedback.title')}</h1>
          <div className="text-sm text-gray-500 mt-1">
            {t('periods.title')}: {getFormattedPeriod()}
          </div>
        </div>
        
        <Flex 
          justify="between" 
          align="center" 
          direction={isMobile ? "column" : "row"}
          mobileAlign="start"
          gap="md"
        >
          {/* Заменяем AnalyticsPeriodSelector на PeriodSelector */}
          <PeriodSelector 
            period={period} 
            onChange={handlePeriodChange} 
            showTitle={false}
            defaultPeriodType="byDay" 
          />
        </Flex>
        
        {error && (
          <Card padding="md" background="light-gray">
            <Card.Body>
              <p className="text-red-600">{error}</p>
            </Card.Body>
          </Card>
        )}
        
        <div className={styles.chartSection}>
          <FeedbackMetrics 
            data={data?.metrics} 
            isLoading={isLoading}
            selectedMetric={selectedMetric}
            onSelectMetric={handleMetricSelect}
          />
        </div>
        
        <div className={styles.chartSection}>
          <Grid 
            cols={1} 
            mobileCols={1} 
            tabletCols={1} 
            gap="md"
          >
            {/* Блок Распределение оценок - передаем выбранную категорию */}
            <Card padding="none" background="white">
              <CategoryDistribution 
                data={data?.distribution || {product_quality: [], service: [], value: []}} 
                isLoading={isLoading}
                activeCategory={getCategoryFromMetric(selectedMetric)}
                hideButtons={true}  // Скрываем кнопки переключения категорий
                period={period}     // Передаем период компоненту
              />
            </Card>
            
            {/* Блок Анализ рекомендаций */}
            <Card padding="none" background="white">
              <RecommendationStats 
                data={data?.recommendation || null} 
                isLoading={isLoading}
              />
            </Card>
            
            {/* Блок Анализ комментариев */}
            <Card padding="none" background="white">
              <CommentsAnalysis 
                data={data?.comments as CommentsData | null}
                isLoading={isLoading}
                period={period}
                businessTimezone={data?.business_timezone}
              />
            </Card>
          </Grid>
        </div>
      </Flex>
    </Container>
  );
};

export default FeedbackSection;